import {
  ComplianceExtraFieldsConfigFragment,
  FinanceValueObjectsComplianceExtraFieldOrganizationEnum,
  UpsertDefaultComplianceSettingDocument,
} from 'generated/resources/graphql'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DefaultComplianceSettingsFormValues } from 'modules/finance/activities/defaultComplianceSettings/components/types'

interface UseDefaultComplianceSettingsFormProps {
  defaultValues?: Partial<DefaultComplianceSettingsFormValues>
  refetchQuery?: string
  onSuccess: () => void
  successMessage: string
  systemComplianceExtraFields: ComplianceExtraFieldsConfigFragment[]
}

export const useDefaultComplianceSettingsForm = ({
  defaultValues,
  refetchQuery,
  onSuccess,
  successMessage,
  systemComplianceExtraFields,
}: UseDefaultComplianceSettingsFormProps) => {
  const [upsertDefaultComplianceSettings, { loading: upserting }] = useMutation(
    UpsertDefaultComplianceSettingDocument,
    {
      extractorFn: (data) => data.financeMutations.upsertFinanceComplianceExtraFieldDefaultSetting,
      refetchQueries: refetchQuery ? [refetchQuery] : undefined,
    }
  )

  const getOrganizationById = (
    id: string | null
  ): FinanceValueObjectsComplianceExtraFieldOrganizationEnum | null => {
    if (!id) return null
    const field = systemComplianceExtraFields.find(
      (systemComplianceExtraField) => systemComplianceExtraField.id === id
    )
    return field ? field.organization : null
  }

  const getCountryById = (id: string | null): string | null => {
    if (!id) return null
    const field = systemComplianceExtraFields.find(
      (systemComplianceExtraField) => systemComplianceExtraField.id === id
    )
    return field ? field.countryCode : null
  }

  const getInitialComplianceFieldId = () => {
    return systemComplianceExtraFields.length === 1
      ? (systemComplianceExtraFields[0]?.id ?? null)
      : null
  }

  const form = useForm<DefaultComplianceSettingsFormValues>({
    defaultValues: {
      legalEntity: null,
      complianceExtraFieldConfigId: getInitialComplianceFieldId(),
      creditNoteDefaultValues: [],
      invoiceDefaultValues: [],
      ...defaultValues,
    },
    validateOnChange: true,
    validate: (data) => {
      const errors: FormErrors<DefaultComplianceSettingsFormValues> = {}

      if (!data.legalEntity) {
        errors.legalEntity = i18n.t(
          'finance.compliance_extra_field_default_settings.modal.legal_entity_required'
        )
      }

      if (!data.complianceExtraFieldConfigId) {
        errors.complianceExtraFieldConfigId = i18n.t(
          'finance.compliance_extra_field_default_settings.modal.tax_agency_required'
        )
      }

      return errors
    },
    onSubmit: async (values) => {
      const organization = getOrganizationById(values.complianceExtraFieldConfigId)
      const country = getCountryById(values.complianceExtraFieldConfigId)
      const creditNoteDefaultValues = values.creditNoteDefaultValues
      const invoiceDefaultValues = values.invoiceDefaultValues

      if (!organization || !country) {
        form.setErrors({
          complianceExtraFieldConfigId: i18n.t(
            'finance.compliance_extra_field_default_settings.modal.tax_agency_required'
          ),
        })
        return { success: false, errors: [] }
      }

      const result = await upsertDefaultComplianceSettings({
        variables: {
          legalEntityId: values.legalEntity?.id ?? '0',
          organization,
          country,
          creditNoteDefaultValues: creditNoteDefaultValues.map((value) => ({
            fieldName: value.fieldName,
            value: value.value,
          })),
          invoiceDefaultValues: invoiceDefaultValues.map((value) => ({
            fieldName: value.fieldName,
            value: value.value,
          })),
        },
      })

      const handled = handleResult(result, (parsedErrors) => {
        app.setFlash({
          type: 'wrong',
          message: parsedErrors.errors ?? i18n.t('errors.generic_error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: successMessage,
        })
        onSuccess()
        return { success: true, data: values }
      } else {
        return {
          errors: [],
          success: false,
        }
      }
    },
  })

  const resetForm = () => {
    form.resetForm()
  }

  return {
    form,
    loading: upserting,
    resetForm,
  }
}
